import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import type {
  AnimationEvent,
} from 'react';
import { usePrevious } from 'react-use';
import { Box } from '@metropolis-io/components';
import type { BoxProps } from '@metropolis-io/components';
import type { TabContextType } from './Tabs.types';
import { useTabContentAnimations } from './useTabContentAnimations';

interface TabContentRendererProps extends BoxProps, TabContextType {}

export function TabContentRenderer({
  activeTabId,
  activeTabIndex,
  prevActiveTabId,
  prevActiveTabIndex,
  tabs,
  tabContentAnimation,
  setActiveTab,
  registerTab,
  unregisterTab,
  ...tabContainerProps
}: TabContentRendererProps) {
  const activeContentRef = useRef<HTMLDivElement>(null);
  const prevTabRef = useRef<HTMLDivElement>(null);
  const [animating, setAnimating] = useState(false);
  const animatingPrevTabIndex = usePrevious(prevActiveTabIndex);

  useEffect(() => {
    if (
      tabContentAnimation
      && tabContentAnimation !== 'none'
      && activeTabIndex !== null
      && prevActiveTabIndex !== null
      && prevActiveTabIndex !== activeTabIndex
      && animatingPrevTabIndex !== prevActiveTabIndex
    ) {
      setAnimating(true);
    }
  }, [
    activeTabIndex,
    animatingPrevTabIndex,
    prevActiveTabIndex,
    animating,
    tabContentAnimation,
  ]);

  const {
    activeTabCss,
    prevTabCss,
  } = useTabContentAnimations({
    activeTabIndex,
    prevActiveTabIndex,
    tabContentAnimation,
  });

  const handleAnimationEnd = useCallback((e: AnimationEvent) => {
    if (e.target === prevTabRef.current) {
      setAnimating(false);
    }
  }, []);

  return (
    <Box
      flexDirection="column"
      alignItems="stretch"
      alignSelf="stretch"
      width="100%"
      overflow="hidden"
      {...tabContainerProps}
    >
      <Box position="relative">
        <Box
          position="relative"
          zIndex={2}
          css={activeTabCss}
          width="100%"
          height="auto"
          key={`tab-content__${activeTabId}`}
          ref={activeContentRef}
        >
          {activeTabIndex !== null ? tabs[activeTabIndex].content : null}
        </Box>
        {animating && (typeof prevTabCss !== 'string') && tabContentAnimation && (
        <Box
          position="absolute"
          top={0}
          left={0}
          zIndex={1}
          ref={prevTabRef}
          onAnimationEnd={handleAnimationEnd}
          width="100%"
          css={prevTabCss}
          key={`tab-content__${prevActiveTabId}`}
        >
          {prevActiveTabIndex !== null ? tabs[prevActiveTabIndex].content : null}
        </Box>
        )}
      </Box>
    </Box>
  );
}
